import { ActionDispatching, ComplexValue, LoadOptions, Value } from 'app/types-shared';
import { WithMetroLineWrappedComponentProps } from 'app/components/metro-line/types';
import { ExternalUrlGroupsTranslation } from '../constants';
import { ExternalUrlGroupsSectionType } from './metroline/types';
import {
  ExternalUrlGroupsFormState, ExternalUrlGroupItem, ExternalUrlGroupsItemValidation,
  ExternalUrlItem,
  ExternalUrlValidation,
  ExternalUrlsValidation,
  AccessPolicyValue,
  AccessPolicyValidation,
  ExternalUrlValue
} from './types-store';
import { ApplicationState } from 'app/types-store';
import { AnyAction } from 'redux';
import { ValidationProps } from 'ui';
import { ExtraFooterButton } from 'ui/common/select/types';

export interface ExternalUrlGroupsItemWire {
  disabled: string;
  code: Value;
  language: Value[];
  sequence: number;
  accessPolicy: AccessPolicyWire[];
  externalUrl: ExternalUrlWire[];
}

export interface ExternalUrlGroupsSectionProps {
  actionType: ExternalUrlGroupsActionType;
  externalUrlGroupItem: ExternalUrlGroupItem;
  options: {
    [key: string]: {
      fetching: boolean;
      values?: ComplexValue<number>[];
    };
  };
  locale: string;
  translation: ExternalUrlGroupsTranslation;
  section: ExternalUrlGroupsSectionType;
  fetching?: boolean;
  translationAdminAccess: boolean;
  externalUrlAccess: boolean;
  validation: ExternalUrlGroupsItemValidation;
  accessPolicyValidation?: AccessPolicyValidation;
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;
  updateItem: (item: Partial<ExternalUrlGroupItem>) => void;
  updateValidation: (validation: ExternalUrlGroupsItemValidation) => void;
  displayError: (errorMessage: string) => void;
  updateAccessPolicyItem: (textValue: AccessPolicyValue) => void;
  removeAccessPolicy: (id: number) => void;
  addAccessPolicy: () => void;
  resetAccessPolicyClientValidation: (id: number, key?: keyof AccessPolicyValue) => void;
  resetAccessPolicyServerValidation: (id: number) => void;
  removeAllAccessPolicy: () => void;
}

export type ExternalUrlGroupsSection = ExternalUrlGroupsSectionProps & WithMetroLineWrappedComponentProps<ExternalUrlGroupsSectionType>;

export interface ExternalUrlGroupsContainerProps {
  actionType: ExternalUrlGroupsActionType;
  externalUrls: ExternalUrlItem[];
  description: string;
  longDescription: string;
  formState: ExternalUrlGroupsFormState;
  shouldPrompt: boolean;
  mainLanguage: number;
  validation: ExternalUrlGroupsItemValidation;
  translation: ExternalUrlGroupsTranslation;
  children?: React.ReactNode;
  switchTranslationsToggle: (value: boolean) => void;
  saveExternalUrlGroup: (externalUrlGroup: ExternalUrlGroupItem) => void;
  deleteExternalUrlGroup: () => void;
  updateValidation: (validation: ExternalUrlGroupsItemValidation) => void;
  displayError: (errorMessage: string) => void;
}

export interface ExternalUrlGroupsFormProps {
  actionType?: ExternalUrlGroupsActionType;
  formState: ExternalUrlGroupsFormState;
  shouldPrompt: boolean;
  mainLanguage: number;
  options: {
    [key: string]: {
      fetching: boolean;
      values?: ComplexValue<number>[];
    };
  };
  externalUrlOptions: {
    fetching: boolean;
    values?: ExternalUrlValue[];
  };
  section: ExternalUrlGroupsSectionType;
  locale: string;
  translation: ExternalUrlGroupsTranslation;
  translationAdminAccess: boolean;
  externalUrlAccess: boolean;
  accessPolicyValidation?: AccessPolicyValidation;
  externalUrlsValidation: ExternalUrlsValidation;
  showTranslations: boolean;
  saveExternalUrlGroup: (item: ExternalUrlGroupItem) => void;
  deleteExternalUrlGroup: () => void;
  updateItem: (item: Partial<ExternalUrlGroupItem>) => void;
  changeLanguage: (languageId: number, text: string) => void;
  copyLanguageToAll: (languageId: number, successMessage: string) => void;
  changeLongDescription: (languageId: number, text: string) => void;
  copyLongDescriptionToAll: (languageId: number, successMessage: string) => void;
  switchTranslationsToggle: (showTranslations: boolean) => void;
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;
  loadExternalUrlOptions: (loadOptionsParams: LoadOptions) => void;
  updateValidation: (validation: ExternalUrlGroupsItemValidation) => void;
  displayError: (errorMessage: string) => void;
  updateAccessPolicyItem: (item: AccessPolicyValue) => void;
  removeAccessPolicy: (id: number) => void;
  addAccessPolicy: () => void;
  resetAccessPolicyClientValidation: (id: number, key?: keyof AccessPolicyValue) => void;
  resetAccessPolicyServerValidation: (id: number) => void;
  removeAllAccessPolicy: () => void;
  addExternalUrls: AddExternalUrlsHandler;
  updateExternalUrl: UpdateExternalUrlItemHandler;
  deleteExternalUrl: (externalUrlId: number) => void;
  deleteAllExternalUrls: () => void;
}

export interface ExternalUrlGroupsLanguageProps extends WithMetroLineWrappedComponentProps<ExternalUrlGroupsSectionType> {
  language: ComplexValue<number>[];
  longDescription: ComplexValue<number>[];
  mainLanguage: number;
  languageOptions: ComplexValue<number>[];
  showTranslations: boolean;
  validation: ExternalUrlGroupsItemValidation;
  translation: ExternalUrlGroupsTranslation;
  section: string;
  switchTranslationsToggle: (value: boolean) => void;
  changeLanguage: (languageId: number, text: string) => void;
  copyLanguageToAll: (languageId: number, successMessage: string) => void;
  changeLongDescription: (languageId: number, text: string) => void;
  copyLongDescriptionToAll: (languageId: number, successMessage: string) => void;
}

export interface ExternalUrlGroupStickyHeaderProps {
  actionType: ExternalUrlGroupsActionType;
  shouldPrompt: boolean;
  externalUrlGroupItem: ExternalUrlGroupItem;
  name: string;
  description: string;
  longDescription: string;
  externalUrls: ExternalUrlItem[];
  fetching: boolean;
  translation: ExternalUrlGroupsTranslation;
  formValidation: ExternalUrlGroupsItemValidation;
  displayError: (errorMessage: string) => void;
  onValidate: () => void;
  onSave: () => void;
  onDelete: () => void;
}

export enum ExternalUrlGroupsActionType {
  add = 'add',
  edit = 'edit'
}

export interface AccessPolicyItemProps extends AccessPolicyTilesProps {
  item: AccessPolicyValue;
}

export interface AccessPolicyTilesProps {
  externalUrlGroupItem: ExternalUrlGroupItem;
  accessPolicyValidation?: AccessPolicyValidation;
  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number>[];
    };
  };
  locale: string;
  validation: ExternalUrlGroupsItemValidation;
  translation: ExternalUrlGroupsTranslation;
  translationAdminAccess: boolean;
  externalUrlAccess: boolean;

  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;
  updateAccessPolicyItem: (textValue: AccessPolicyValue) => void;
  removeAccessPolicy: (id: number) => void;
  removeAllAccessPolicy: () => void;
  addAccessPolicy: () => void;
  resetAccessPolicyClientValidation: (id: number, key?: keyof AccessPolicyValue) => void;
  resetAccessPolicyServerValidation: (id: number) => void;
}

export interface AccessPolicyWire {
  caseType: Value;
  operation: Value;
}

export interface ExternalUrlWire {
  code: Value;
  sequence: number;
  disabled: string;
  urlTemplate: string;
}

export enum AddExternalUrlType {
  DEFAULT,
  ITEM_SEQUENCE
}

export type AddExternalUrlsHandler = (
  addType: AddExternalUrlType,
  externalUrls: ExternalUrlItem[],
  sequence: number
) => ActionDispatching<AnyAction, ApplicationState>;

export type UpdateExternalUrlItemHandler = (
  currentExternalUrlId: number,
  externalUrlItem: ExternalUrlItem,
  validation: ExternalUrlValidation
) => void;


export interface AccessPolicyApplicationState {
  loadedOptions: LoadOptions;
}

export interface AccessPolicyApplicationProps {
  id: string;
  label: string;
  required?: boolean;
  searchRequired?: boolean;
  enableMultilineValidation?: boolean;
  options: ComplexValue<number>[];
  fetchingOptions: boolean;
  translation: ExternalUrlGroupsTranslation;
  displayCategories?: boolean;
  categoryOptions?: ComplexValue<number>[];
  categoryId?: number;
  includeDisabledLabel?: string;
  uncheckSelectItem?: boolean;
  valueItem: ComplexValue<number>;
  validation?: ValidationProps;
  readOnly?: boolean;
  showBackgroundColour?: boolean;
  className?: string;
  showSpinnerLabel?: boolean;
  size?: number;
  minLength?: number;
  onLoadCategoryOptions?: (loadOptions: LoadOptions) => void;
  onLoadOptions: (id: string, loadOptionsParams: LoadOptions) => void;
  onChange: (value: ComplexValue<number>) => void;
  extraFooterButton?: ExtraFooterButton;
}
